import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { SITE_NAME, INSTAGRAM_URL, BRAND_COLOR } from '../constants';

const Navbar: React.FC<{ isDarkMode: boolean; toggleTheme: () => void }> = ({ isDarkMode, toggleTheme }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'work', 'about', 'services', 'contact'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (id: string) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    setIsOpen(false);
  };

  const nameParts = SITE_NAME.split(' ');

  return (
    <>
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 z-[130] origin-left" 
        style={{ scaleX, backgroundColor: BRAND_COLOR }} 
      />
      <nav className={`fixed top-0 left-0 right-0 z-[120] transition-all duration-500 py-6 
        ${isScrolled 
          ? (isDarkMode ? 'bg-black/90 border-white/5 shadow-2xl' : 'bg-white/90 border-zinc-200 shadow-lg') + ' backdrop-blur-md border-b' 
          : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <button onClick={() => handleNav('home')} className={`font-display text-2xl font-black uppercase tracking-tighter transition-colors ${isDarkMode ? 'text-white' : 'text-black'}`}>
            {nameParts[0]} <span style={{ color: BRAND_COLOR }}>{nameParts.slice(1).join(' ')}</span>
          </button>
          
          <div className={`hidden md:flex items-center space-x-12 text-[15px] font-black tracking-[0.3em] uppercase transition-colors`}>
            {['work', 'about', 'services', 'contact'].map(i => (
              <button 
                key={i} 
                onClick={() => handleNav(i)} 
                className={`transition-all relative py-2 ${activeSection === i ? (isDarkMode ? 'text-white opacity-100' : 'text-black opacity-100') : (isDarkMode ? 'text-white opacity-40 hover:opacity-100' : 'text-black opacity-40 hover:opacity-100')}`}
              >
                {i}
                {activeSection === i && (
                  <motion.div layoutId="nav-underline" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand" />
                )}
              </button>
            ))}
            
            <button 
              onClick={toggleTheme} 
              className={`p-2 rounded-full transition-all active:scale-90 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}
              aria-label="Toggle Theme"
            >
              {isDarkMode ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BRAND_COLOR} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BRAND_COLOR} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>

            <a href={INSTAGRAM_URL} target="_blank" style={{ backgroundColor: BRAND_COLOR }} className="text-black px-10 py-3 hover:brightness-110 transition-all font-black text-[11px] shadow-sm">BOOK</a>
          </div>

          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} aria-label="Menu">
            <div className={`w-7 h-0.5 mb-2 transition-all ${isDarkMode ? 'bg-white' : 'bg-black'} ${isOpen ? 'rotate-45 translate-y-2.5' : ''}`} />
            <div className={`w-7 h-0.5 transition-all ${isDarkMode ? 'bg-white' : 'bg-black'} ${isOpen ? '-rotate-45 -translate-y-2.5' : ''}`} />
          </button>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25 }} className={`fixed inset-0 flex flex-col justify-center px-10 space-y-10 z-[110] transition-colors duration-500 ${isDarkMode ? 'bg-black' : 'bg-white'}`}>
              {['work', 'about', 'services', 'contact'].map(i => (
                <button key={i} onClick={() => handleNav(i)} className={`font-display text-6xl font-black uppercase tracking-tighter text-left ${isDarkMode ? 'text-white' : 'text-black'}`}>{i}</button>
              ))}
              <div className="flex flex-col gap-6 pt-10 border-t border-zinc-100 dark:border-white/10">
                <button onClick={toggleTheme} style={{ color: BRAND_COLOR }} className="text-left font-black tracking-widest text-2xl">
                  {isDarkMode ? 'LIGHT MODE' : 'DARK MODE'}
                </button>
                <a href={INSTAGRAM_URL} target="_blank" style={{ backgroundColor: BRAND_COLOR }} className="py-6 text-black font-black uppercase text-center text-xl">DM TO BOOK</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
